import { Fragment, memo, useEffect, useRef } from "react";
import { message } from "antd";
import {
	FileText,
	ExternalLink,
	BookOpen,
	MapPin,
	CheckCircle2,
	AlertTriangle,
	XCircle,
	Info,
	AlertCircle,
	ShieldCheck,
	Scale,
	Link2
} from "lucide-react";

import ModalNavControls from "components/modal-nav-controls/modal-nav-controls";
import CodeSectionPanel, {
	CodeSectionReference,
	CrossReferencedCodeSection,
	crossReferenceToCodeSectionReference
} from "components/code-section-panel/code-section-panel";
import PlanReviewFeedbackSection, {
	PlanReviewFeedbackRow,
	PlanReviewFeedbackSubmission
} from "components/plan-review-feedback-section/plan-review-feedback-section";
import { canSeeReviewerTools, PlanReviewFindingStatus } from "constants/field-values";
import { useAddPlanReviewFeedbackMutation } from "graphql/mutations/add-plan-review-feedback.generated";
import { useUpdatePlanReviewFeedbackMutation } from "graphql/mutations/update-plan-review-feedback.generated";
import { useDeletePlanReviewFeedbackMutation } from "graphql/mutations/delete-plan-review-feedback.generated";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { toExternalUrl } from "utils/strings";

// Shared Apollo options for the three feedback mutations. errorPolicy "none" makes the
// promise reject on any GraphQL error, so each handler's try/catch is the single source
// of truth for success/failure messaging.
const FEEDBACK_MUTATION_OPTIONS = {
	errorPolicy: "none",
	fetchPolicy: "network-only",
	notifyOnNetworkStatusChange: true
} as const;

// The finding shape the report already has from the job subscription, narrowed to what
// the modal renders. Declared structurally so the caller can pass its row straight in.
export interface PlanReviewFindingModalItem {
	id: string;
	job_id: string;
	status?: string | null;
	severity?: string | null;
	confidence?: number | null;
	category?: string | null;
	/** The category as the jurisdiction names it today; `category` is what the report recorded. */
	category_record?: { name: string } | null;
	title?: string | null;
	summary?: string | null;
	corrective_action?: string | null;
	sheet_reference?: string | null;
	page_number?: number | null;
	/** Superseded by sheet_reference + page_number; still set on findings analyzed before the split. */
	page_reference?: string | null;
	rule_id?: string | null;
	requirement?: {
		id: string;
		code?: string | null;
		title?: string | null;
		category_record?: { name: string } | null;
		requirement_type?: string | null;
		source?: string | null;
		section?: string | null;
		source_url?: string | null;
		/** The adopted code the rule rests on. The analysis judged the package against
		 *  this text as well as the rule, so the reviewer is shown the same thing. */
		plan_review_requirement_code_sections?: CodeSectionReference[];
		/** The sections that those code sections refer to. The analysis read them too. */
		cross_referenced_sections?: CrossReferencedCodeSection[];
	} | null;
	document?: {
		id: string;
		file_name?: string | null;
	} | null;
	plan_review_citations?: {
		id: string;
		tier?: string | null;
		source?: string | null;
		section?: string | null;
		source_url?: string | null;
		page_numbers?: unknown;
	}[];
	plan_review_feedbacks?: PlanReviewFeedbackRow[];
}

interface PlanReviewFindingModalProps {
	isOpen: boolean;
	onClose: () => void;
	item: PlanReviewFindingModalItem | null;
	/** Step to the previous/next report item without leaving the modal. Both are optional:
	 *  when the caller has no list to walk, only the close button renders. */
	onPrevious?: () => void;
	onNext?: () => void;
	hasPrevious?: boolean;
	hasNext?: boolean;
	/** The submitted document this finding points at, when it has one. */
	document?: {
		id: string;
		file_name?: string | null;
		file_url?: string | null;
		mime_type?: string | null;
		file_size_bytes?: number | null;
		doc_type?: string | null;
	} | null;
}

const STATUS_META: Record<string, { label: string; className: string; icon: typeof CheckCircle2 }> = {
	[PlanReviewFindingStatus.PASS]: { label: "Pass", className: "prfm-status-pass", icon: CheckCircle2 },
	[PlanReviewFindingStatus.WARNING]: { label: "Warning", className: "prfm-status-warning", icon: AlertTriangle },
	[PlanReviewFindingStatus.FAIL]: { label: "Fail", className: "prfm-status-fail", icon: XCircle },
	[PlanReviewFindingStatus.MISSING]: { label: "Missing", className: "prfm-status-missing", icon: AlertCircle },
	[PlanReviewFindingStatus.NOT_APPLICABLE]: { label: "Not Applicable", className: "prfm-status-skipped", icon: Info }
};

const DOC_TYPE_LABELS: Record<string, string> = {
	plans: "Plans",
	calculations: "Calculations",
	cut_sheets: "Cut Sheets",
	application_form: "Application Form",
	cover_letter: "Cover Letter",
	other: "Document"
};

const formatBytes = (bytes?: number | null): string => {
	if (!bytes) return "";
	const units = ["B", "KB", "MB", "GB"];
	const i = Math.floor(Math.log(bytes) / Math.log(1024));
	return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

// page_numbers is a jsonb column, so it arrives as a parsed array or as a string.
// Anything that is not a usable page is dropped: the numbers become links, and a link
// to page 0 or to "12-14" would just open the file at the wrong place.
const parsePageNumbers = (raw: unknown): number[] => {
	if (!raw) return [];
	const list = Array.isArray(raw)
		? raw
		: (() => {
				try {
					const parsed = JSON.parse(String(raw));
					return Array.isArray(parsed) ? parsed : [];
				} catch {
					return [];
				}
			})();
	const pages = list.map(Number).filter(page => Number.isInteger(page) && page > 0);
	return [...new Set(pages)];
};

// The #page anchor is read by the browser's PDF viewer, so it only helps on a file it
// opens as a PDF; on anything else the plain URL is kept.
const isPdf = (url?: string | null, mimeType?: string | null): boolean =>
	mimeType === "application/pdf" || url?.split("?")[0]?.toLowerCase().endsWith(".pdf") === true;

const atPage = (url: string, page?: number | null): string => (page ? `${url}#page=${page}` : url);

// Where the finding is, as the reviewer reads it. The sheet printed on the drawing and
// the page inside the PDF are different numbers — sheet 27 is often page 31 — so both
// are always shown when both are known. Findings analyzed before the two were split
// only have the sentence the model wrote.
const formatLocation = (item: PlanReviewFindingModalItem): string => {
	const sheet = item.sheet_reference?.trim();
	const parts: string[] = [];
	if (sheet) parts.push(`Sheet ${sheet}`);
	if (item.page_number) parts.push(`Page ${item.page_number}`);
	return parts.join(" · ") || (item.page_reference ?? "");
};

const confidenceLabel = (confidence?: number | null): string => {
	if (confidence === null || confidence === undefined) return "—";
	if (confidence >= 0.8) return "High";
	if (confidence >= 0.5) return "Medium";
	return "Low";
};

/**
 * Detail view for one Review Category check, opened from the compliance report.
 *
 * Mirrors the Ollie EvidenceModal: the referenced document at the top, then the
 * reviewer's observation, the compliance verdict and the citation behind it. The
 * document section is hidden when the finding is not tied to one — which is the normal
 * case for a MISSING finding, where the point is that no document was submitted.
 */
const PlanReviewFindingModal = ({
	isOpen,
	onClose,
	item,
	document: submittedDocument,
	onPrevious,
	onNext,
	hasPrevious,
	hasNext
}: PlanReviewFindingModalProps) => {
	const { loggedUserValue } = useLoggedUser();
	const currentUserId = loggedUserValue?.id;
	const bodyRef = useRef<HTMLDivElement>(null);

	const [addPlanReviewFeedbackMutation, { loading: adding }] = useAddPlanReviewFeedbackMutation({
		...FEEDBACK_MUTATION_OPTIONS
	});
	const [updatePlanReviewFeedbackMutation, { loading: updating }] = useUpdatePlanReviewFeedbackMutation({
		...FEEDBACK_MUTATION_OPTIONS
	});
	const [deletePlanReviewFeedbackMutation, { loading: deleting }] = useDeletePlanReviewFeedbackMutation({
		...FEEDBACK_MUTATION_OPTIONS
	});

	// Escape closes, and the page behind must not scroll while the modal is up.
	useEffect(() => {
		if (!isOpen) return;
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") onClose();
		};
		window.addEventListener("keydown", onKeyDown);
		const previousOverflow = window.document.body.style.overflow;
		window.document.body.style.overflow = "hidden";
		return () => {
			window.removeEventListener("keydown", onKeyDown);
			window.document.body.style.overflow = previousOverflow;
		};
	}, [isOpen, onClose]);

	// Stepping with Previous/Next keeps the modal mounted, so the body has to be sent back to
	// the top by hand or the next finding opens part-way down.
	useEffect(() => {
		bodyRef.current?.scrollTo({ top: 0 });
	}, [item?.id]);

	const categoryLabel = item?.category_record?.name ?? item?.category ?? null;

	// One feedback per finding, newest first: the latest row is the one shown and edited.
	const latestFeedback = item?.plan_review_feedbacks?.[0] ?? null;
	// Plan reviews are the AHJ's call, so only its reviewers (and the platform admin) rate the AI's work.
	const canSeeReviewerOnlyContent = canSeeReviewerTools(loggedUserValue?.role);
	const canGiveFeedback = !!currentUserId && !!item && canSeeReviewerOnlyContent;

	const handleSubmitFeedback = async ({
		decision,
		errorAttribution,
		comment,
		editingFeedbackId
	}: PlanReviewFeedbackSubmission): Promise<boolean> => {
		if (!currentUserId || !item) return false;
		try {
			if (editingFeedbackId) {
				// Editing: update the existing row in place rather than inserting a duplicate.
				// user_id is rewritten to the current reviewer so they become the row's owner.
				const { data } = await updatePlanReviewFeedbackMutation({
					variables: {
						id: editingFeedbackId,
						_set: {
							user_id: currentUserId,
							decision,
							error_attribution: errorAttribution,
							comment,
							updated_at: new Date().toISOString()
						}
					}
				});
				// A row outside the reviewer's permissions comes back as null instead of an
				// error, which would otherwise read as a save that silently did nothing.
				if (!data?.update_plan_review_feedback_by_pk) {
					message.error("You can't edit this feedback.");
					return false;
				}
				message.success("Your feedback was updated.");
			} else {
				await addPlanReviewFeedbackMutation({
					variables: {
						input: {
							job_id: item.job_id,
							finding_id: item.id,
							user_id: currentUserId,
							decision,
							error_attribution: errorAttribution,
							comment
						}
					}
				});
				message.success("Thanks — your feedback was recorded.");
			}
			return true;
		} catch (error) {
			console.error("Failed to record feedback:", error);
			message.error("Couldn't save your feedback. Please try again.");
			return false;
		}
	};

	const handleDeleteFeedback = async (): Promise<boolean> => {
		if (!latestFeedback) return false;
		try {
			const { data } = await deletePlanReviewFeedbackMutation({ variables: { id: latestFeedback.id } });
			if (!data?.delete_plan_review_feedback_by_pk) {
				message.error("You can't remove this feedback.");
				return false;
			}
			message.success("Your feedback was removed.");
			return true;
		} catch (error) {
			console.error("Failed to remove feedback:", error);
			message.error("Couldn't remove your feedback. Please try again.");
			return false;
		}
	};

	if (!isOpen || !item) return null;

	const status = item.status ?? PlanReviewFindingStatus.PASS;
	const meta = STATUS_META[status] ?? STATUS_META[PlanReviewFindingStatus.NOT_APPLICABLE];
	const StatusIcon = meta.icon;
	const codeSections = item.requirement?.plan_review_requirement_code_sections ?? [];
	const crossReferencedSections = (item.requirement?.cross_referenced_sections ?? []).map(
		crossReferenceToCodeSectionReference
	);
	const hasOneCrossReference = crossReferencedSections.length === 1;
	const citation = item.plan_review_citations?.[0] ?? null;
	const citationSource = citation?.source ?? item.requirement?.source ?? null;
	const citationSection = citation?.section ?? item.requirement?.section ?? null;
	const citationUrl = toExternalUrl(citation?.source_url ?? item.requirement?.source_url);
	const citationPages = parsePageNumbers(citation?.page_numbers);

	const location = formatLocation(item);
	const documentIsPdf = isPdf(submittedDocument?.file_url, submittedDocument?.mime_type);
	const documentPdfUrl = documentIsPdf ? (submittedDocument?.file_url ?? null) : null;
	const sourcePdfUrl = citationUrl && isPdf(citationUrl) ? citationUrl : null;

	// The finding's own page is where the reviewer wants to land; a cited page is the
	// fallback for findings that only pinned the citation.
	const documentOpenPage = item.page_number ?? citationPages[0] ?? null;

	// The cited pages are pages of the submitted document, so that is what they open.
	// Only a finding without one — a MISSING check, where nothing was submitted — cites
	// pages of the source itself.
	const pagesTargetUrl = submittedDocument ? documentPdfUrl : sourcePdfUrl;

	return (
		<div className="prfm-overlay" onClick={onClose} role="presentation">
			<div
				className="prfm-modal"
				onClick={event => event.stopPropagation()}
				role="dialog"
				aria-modal="true"
				aria-label={item.title ?? "Finding details"}
			>
				<header className="prfm-header">
					<div className="prfm-header-main">
						<span className={`prfm-status ${meta.className}`}>
							<StatusIcon size={14} />
							{meta.label}
						</span>
						{item.severity && status !== PlanReviewFindingStatus.PASS && (
							<span className="prfm-severity">{item.severity}</span>
						)}
						{categoryLabel && <span className="prfm-category">{categoryLabel}</span>}
					</div>
					<ModalNavControls
						onPrevious={onPrevious}
						onNext={onNext}
						hasPrevious={hasPrevious}
						hasNext={hasNext}
						onClose={onClose}
					/>
				</header>

				<div className="prfm-body" ref={bodyRef}>
					<h2 className="prfm-title">{item.title ?? "Finding"}</h2>

					{submittedDocument && (
						<section className="prfm-section">
							<h3 className="prfm-section-title">
								<FileText size={15} />
								Submitted Document
							</h3>
							<div className="prfm-doc">
								<div className="prfm-doc-icon">
									<FileText size={20} />
								</div>
								<div className="prfm-doc-info">
									<p className="prfm-doc-name">{submittedDocument.file_name}</p>
									<p className="prfm-doc-meta">
										{DOC_TYPE_LABELS[submittedDocument.doc_type ?? "other"] ?? "Document"}
										{submittedDocument.file_size_bytes ? ` · ${formatBytes(submittedDocument.file_size_bytes)}` : ""}
										{location ? ` · ${location}` : ""}
									</p>
								</div>
								{submittedDocument.file_url && (
									<a
										className="prfm-doc-open"
										href={
											documentIsPdf ? atPage(submittedDocument.file_url, documentOpenPage) : submittedDocument.file_url
										}
										target="_blank"
										rel="noreferrer"
									>
										Open
										<ExternalLink size={14} />
									</a>
								)}
							</div>
						</section>
					)}

					<section className="prfm-section">
						<h3 className="prfm-section-title">
							<BookOpen size={15} />
							Observation
						</h3>
						<p className="prfm-text">{item.summary ?? "No observation was recorded for this check."}</p>
						{!submittedDocument && location && (
							<p className="prfm-page-reference">
								<MapPin size={13} />
								{location}
							</p>
						)}
					</section>

					<section className="prfm-section">
						<h3 className="prfm-section-title">
							<ShieldCheck size={15} />
							Compliance
						</h3>
						<div className="prfm-compliance">
							<div className="prfm-compliance-row">
								<span className="prfm-label">Status</span>
								<span className={`prfm-status ${meta.className}`}>
									<StatusIcon size={14} />
									{meta.label}
								</span>
							</div>
							{item.severity && status !== PlanReviewFindingStatus.PASS && (
								<div className="prfm-compliance-row">
									<span className="prfm-label">Severity</span>
									<span className="prfm-value">{item.severity}</span>
								</div>
							)}
							<div className="prfm-compliance-row">
								<span className="prfm-label">Confidence</span>
								<span className="prfm-value">{confidenceLabel(item.confidence)}</span>
							</div>
							{item.requirement?.code && (
								<div className="prfm-compliance-row">
									<span className="prfm-label">Requirement</span>
									<span className="prfm-value">
										{item.requirement.code}
										{item.requirement.title ? ` — ${item.requirement.title}` : ""}
									</span>
								</div>
							)}
						</div>
						{item.corrective_action && item.corrective_action !== "null" && (
							<div className="prfm-action">
								<p className="prfm-action-label">Recommended action</p>
								<p className="prfm-text">{item.corrective_action}</p>
							</div>
						)}
					</section>

					{codeSections.length > 0 && (
						<section className="prfm-section">
							<h3 className="prfm-section-title">
								<Scale size={15} />
								Code sections applied
							</h3>
							<p className="prfm-code-intro">
								The requirement above is derived from {codeSections.length === 1 ? "this section" : "these sections"} of
								the code. The submittal was reviewed against both.
							</p>
							<CodeSectionPanel references={codeSections} defaultExpanded hideSourceLink={!canSeeReviewerOnlyContent} />
						</section>
					)}

					{/* The sections that the cited code refers to, for example "installed in accordance with
					    Section 903.3.1.1". The analysis read them with the rule, so the reader sees them too. */}
					{crossReferencedSections.length > 0 && (
						<section className="prfm-section">
							<h3 className="prfm-section-title">
								<Link2 size={15} />
								Cross-referenced code sections
							</h3>
							<p className="prfm-code-intro">
								The code sections above refer to {hasOneCrossReference ? "this section" : "these sections"} of the code
								adopted by this jurisdiction. Ollie read {hasOneCrossReference ? "it" : "them"} with the requirement.
							</p>
							<CodeSectionPanel references={crossReferencedSections} hideSourceLink={!canSeeReviewerOnlyContent} />
						</section>
					)}

					{(citationSource || item.rule_id) && (
						<section className="prfm-section">
							<h3 className="prfm-section-title">
								<BookOpen size={15} />
								Citation
							</h3>
							<div className="prfm-citation">
								<p className="prfm-citation-source">
									{citationSource ?? item.rule_id}
									{citationSection ? ` § ${citationSection}` : ""}
								</p>
								{citationPages.length > 0 && (
									<p className="prfm-citation-pages">
										{citationPages.length > 1 ? "Pages " : "Page "}
										{citationPages.map((page, index) => (
											<Fragment key={page}>
												{index > 0 && ", "}
												{pagesTargetUrl ? (
													<a
														className="prfm-citation-page"
														href={atPage(pagesTargetUrl, page)}
														target="_blank"
														rel="noreferrer"
													>
														{page}
													</a>
												) : (
													page
												)}
											</Fragment>
										))}
									</p>
								)}
								{citationUrl && (
									<a className="prfm-citation-link" href={citationUrl} target="_blank" rel="noreferrer">
										View source
										<ExternalLink size={13} />
									</a>
								)}
							</div>
						</section>
					)}

					{canGiveFeedback && (
						<PlanReviewFeedbackSection
							key={item.id}
							savedFeedback={latestFeedback}
							submitting={adding || updating}
							deleting={deleting}
							onSubmit={handleSubmitFeedback}
							onDelete={handleDeleteFeedback}
						/>
					)}
				</div>
			</div>
		</div>
	);
};

export default memo(PlanReviewFindingModal);
